<template>
  <div
    v-if="visible && entry"
    class="modal fade show d-block"
    tabindex="-1"
    style="background-color: rgba(0, 0, 0, 0.5)"
    @click.self="close"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-exchange-alt me-2"></i>
            {{
              $t("exchangeRates.modal.title", {
                from: currencyLabel,
                to: reportingLabel,
              })
            }}
          </h5>
          <button
            type="button"
            class="btn-close"
            :disabled="saving"
            @click="close"
          ></button>
        </div>

        <div class="modal-body">
          <!-- De dónde sale el valor que usa la operación -->
          <div class="mb-3 d-flex align-items-center gap-2 flex-wrap">
            <span class="badge" :class="sourceBadge.class">{{
              sourceBadge.text
            }}</span>
            <small
              v-if="entry.manual && entry.manual.setByName"
              class="text-muted"
            >
              {{
                $t("exchangeRates.modal.setBy", {
                  name: entry.manual.setByName,
                  date: formatDate(entry.manual.setAt),
                })
              }}
            </small>
            <small v-else-if="entry.frozen" class="text-muted">
              {{
                $t("exchangeRates.modal.frozenHint", {
                  date: formatDate(entry.frozen.date),
                })
              }}
            </small>
          </div>

          <!-- Cotización oficial de referencia -->
          <div class="small mb-3 p-2 rounded bg-light border">
            <template v-if="entry.official">
              <div>
                <strong>{{ officialSourceLabel }}</strong>
                ·
                {{
                  $t("exchangeRates.modal.officialOf", {
                    date: formatDate(entry.official.date),
                  })
                }}
              </div>
              <div class="tabular-nums">
                1 {{ currencyLabel }} = {{ formatRate(entry.official.rate) }}
                {{ reportingLabel }}
                <span class="text-muted ms-2">
                  (1 {{ reportingLabel }} =
                  {{ formatRate(1 / entry.official.rate) }} {{ currencyLabel }})
                </span>
              </div>
            </template>
            <div v-else class="text-warning">
              <i class="fas fa-exclamation-triangle me-1"></i>
              {{ $t("exchangeRates.modal.noOfficial") }}
            </div>
          </div>

          <!-- Tipo de cambio de la operación: con candado, por defecto bloqueado -->
          <label class="form-label small fw-semibold mb-1">
            {{
              $t("exchangeRates.modal.operationRate", {
                from: currencyLabel,
                to: reportingLabel,
              })
            }}
          </label>
          <div class="input-group">
            <button
              type="button"
              class="btn"
              :class="locked ? 'btn-outline-secondary' : 'btn-warning'"
              :title="
                locked
                  ? $t('exchangeRates.modal.unlockHint')
                  : $t('exchangeRates.modal.lockHint')
              "
              :disabled="saving"
              @click="toggleLock"
            >
              <i :class="locked ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
            </button>
            <span class="input-group-text">1 {{ currencyLabel }} =</span>
            <input
              ref="rateInput"
              v-model="draftRate"
              type="number"
              step="0.0001"
              min="0"
              class="form-control text-end tabular-nums"
              :disabled="locked || saving"
              @keyup.enter="save"
            />
            <span class="input-group-text">{{ reportingLabel }}</span>
          </div>
          <small v-if="!locked" class="text-muted d-block mt-1">
            {{ $t("exchangeRates.modal.customHint") }}
          </small>
          <small
            v-if="draftInverse"
            class="text-muted d-block mt-1 tabular-nums"
          >
            1 {{ reportingLabel }} = {{ formatRate(draftInverse) }}
            {{ currencyLabel }}
          </small>
          <div
            v-if="error"
            class="alert alert-danger small py-1 px-2 mt-2 mb-0"
          >
            {{ error }}
          </div>
        </div>

        <div class="modal-footer">
          <button
            v-if="entry.manual"
            type="button"
            class="btn btn-outline-primary me-auto"
            :disabled="saving"
            @click="restoreOfficial"
          >
            <i class="fas fa-undo me-1"></i>
            {{ $t("exchangeRates.modal.restoreOfficial") }}
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="saving"
            @click="close"
          >
            {{ $t("exchangeRates.modal.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="!canSave"
            @click="save"
          >
            <i
              :class="
                saving ? 'fas fa-spinner fa-spin me-1' : 'fas fa-check me-1'
              "
            ></i>
            {{ $t("exchangeRates.modal.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { invalidateOperationRates } from "../utils/operationRates";

/**
 * Tipo de cambio de una moneda en una operación (ticket #397).
 *
 * Muestra la cotización oficial (BCRA, o Frankfurter si el BCRA no respondió) y el valor
 * que usa la operación. El input arranca bloqueado: con el candado cerrado la operación
 * sigue al oficial; abrirlo es fijar una cotización propia, que vale para toda la
 * operación y queda marcada como «Personalizada».
 *
 * `entry` es una fila de `fx.rates` del balance (exchange_rates_module).
 */
export default {
  name: "OperationExchangeRateModal",
  props: {
    visible: { type: Boolean, default: false },
    refModel: { type: String, default: "shipping" },
    refId: { type: String, default: "" },
    entry: { type: Object, default: null },
    currencyLabel: { type: String, default: "" },
    reportingLabel: { type: String, default: "USD" },
  },
  data() {
    return {
      locked: true,
      draftRate: "",
      saving: false,
      error: "",
    };
  },
  computed: {
    sourceBadge() {
      const source = this.entry && this.entry.source;
      if (source === "manual")
        return {
          class: "bg-warning text-dark",
          text: this.$t("exchangeRates.source.manual"),
        };
      if (source === "frozen")
        return {
          class: "bg-secondary",
          text: this.$t("exchangeRates.source.frozen"),
        };
      if (source === "settings")
        return {
          class: "bg-info text-dark",
          text: this.$t("exchangeRates.source.settings"),
        };
      if (source === "official")
        return { class: "bg-success", text: this.officialSourceLabel };
      return {
        class: "bg-danger",
        text: this.$t("exchangeRates.source.missing"),
      };
    },
    officialSourceLabel() {
      const source =
        this.entry && this.entry.official && this.entry.official.source;
      return source === "frankfurter"
        ? this.$t("exchangeRates.source.frankfurter")
        : this.$t("exchangeRates.source.bcra");
    },
    draftNumber() {
      const n = Number(this.draftRate);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    draftInverse() {
      return this.draftNumber ? 1 / this.draftNumber : null;
    },
    canSave() {
      if (this.saving || this.locked || !this.draftNumber) return false;
      return !(
        this.entry &&
        this.entry.source === "manual" &&
        this.draftNumber === Number(this.entry.rate)
      );
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(open) {
        if (open) this.reset();
      },
    },
    entry() {
      if (this.visible) this.reset();
    },
  },
  methods: {
    reset() {
      this.locked = true;
      this.error = "";
      this.saving = false;
      this.draftRate =
        this.entry && this.entry.rate != null
          ? this.roundRate(this.entry.rate)
          : "";
    },
    roundRate(value) {
      return Math.round(Number(value) * 1e6) / 1e6;
    },
    formatRate(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "-";
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      });
    },
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "-";
      // La cotización es de un día (guardada a las 00:00 UTC): no correrla por zona horaria.
      return d.toLocaleDateString(undefined, { timeZone: "UTC" });
    },
    toggleLock() {
      this.locked = !this.locked;
      this.error = "";
      if (this.locked) {
        this.reset();
        return;
      }
      this.$nextTick(
        () => this.$refs.rateInput && this.$refs.rateInput.focus()
      );
    },
    close() {
      if (!this.saving) this.$emit("close");
    },
    async persist(rate) {
      if (!this.refId || !this.entry) return;
      this.saving = true;
      this.error = "";
      try {
        const { data } = await wencesApi.put(
          `/api/exchange-rates/operations/${this.refModel}/${this.refId}/${this.entry.currencyId}`,
          { rate }
        );
        invalidateOperationRates(this.refId);
        this.$emit("saved", (data && data.data) || []);
      } catch (e) {
        this.error =
          (e && e.response && e.response.data && e.response.data.error) ||
          this.$t("exchangeRates.modal.saveError");
      } finally {
        this.saving = false;
      }
    },
    save() {
      if (this.canSave) this.persist(this.draftNumber);
    },
    restoreOfficial() {
      this.persist(null);
    },
  },
};
</script>
